import { Search } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import { cn } from "src/lib/utils";
import { useDebounce } from "src/hooks/useDebounce";

interface SearchFieldProps {
	/** Query-param name the term is written to. */
	name?: string;
	placeholder?: string;
	className?: string;
}

/**
 * Search box backed by the URL query string, so a search survives reload and back/forward.
 * Resets to page 1 on every new term.
 */
const SearchField = ({ name = "search", placeholder = "Search...", className }: SearchFieldProps) => {
	const [searchParams, setSearchParams] = useSearchParams();

	const handleSearch = useDebounce((term: string) => {
		const next = new URLSearchParams(searchParams);
		next.set("page", "1");
		if (term) next.set(name, term);
		else next.delete(name);
		setSearchParams(next);
	}, 500);

	return (
		<div className={cn("tw-relative tw-w-full sm:tw-w-[260px]", className)}>
			<Search className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-h-4 tw-w-4 tw-text-text-soft" />
			<input
				id={name}
				name={name}
				type="search"
				placeholder={placeholder}
				defaultValue={searchParams.get(name) ?? ""}
				onChange={(event) => handleSearch(event.target.value.trim())}
				className="tw-h-10 tw-w-full tw-rounded-[10px] tw-border tw-border-border tw-bg-white tw-pl-9 tw-pr-3 tw-text-[13.5px] tw-text-text tw-transition-colors placeholder:tw-text-text-soft focus:tw-border-teal focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-teal/20"
			/>
		</div>
	);
};

export default SearchField;
